import type { DealSubmissionAiFeedbackResult } from "../../api/types";

// Broker New Deal Submission — in-flight AI evaluation tracker.
//
// The "Evaluate & Feedback" POST is synchronous (it can take minutes) but the
// section pane unmounts whenever the broker switches sections (SectionPane is
// keyed by section id). Local `evaluating` state was therefore lost on
// navigation: a broker could leave a running evaluation, return, and fire a
// second one. This tracker lives in DealSubmissionRunner — which stays mounted
// across section switches — so an evaluation in progress is detectable when the
// broker returns: the button stays disabled/"Evaluating".
//
// On completion the tracker ALSO retains the result per key, so the feedback
// renders immediately on return without waiting for (or depending on) a
// submission reload — the reload still fires to refresh persisted-derived state
// (freshness gate), but it never gates the spinner clearing or the result
// display, which is what previously made a finished evaluation appear to "load
// forever" until a manual refresh.
export interface EvaluationTracker {
  /** Whether an evaluation keyed by `key` is currently running. */
  isEvaluating: (key: string) => boolean;
  /** The most recent completed result for `key`, if any (null while none). */
  result: (key: string) => DealSubmissionAiFeedbackResult | null;
  /**
   * Run `fn` under `key`, de-duplicating concurrent runs (a second call with a
   * live key is a no-op). `fn` resolves with the feedback to retain for the key
   * (or null); the tracker records it and triggers a non-blocking reload when
   * the run settles. `fn` surfaces its own errors.
   */
  run: (
    key: string,
    fn: () => Promise<DealSubmissionAiFeedbackResult | null>,
  ) => Promise<void>;
}

// Default for components rendered outside the runner (e.g. unit tests): runs
// the evaluation inline with no cross-navigation gate or retained result. The
// runner always passes the real tracker.
export const passthroughEvaluationTracker: EvaluationTracker = {
  isEvaluating: () => false,
  result: () => null,
  run: async (_key, fn) => {
    await fn();
  },
};

export function sectionEvalKey(sectionId: string): string {
  return `section:${sectionId}`;
}

export function documentEvalKey(documentId: string): string {
  return `document:${documentId}`;
}
